import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { PatientSearchDropdown } from '@/components/messages/patient-search-dropdown';
import { switchThreadPatient } from '@/lib/api/admin';

/**
 * Re-point an open thread from its current patient to a different one, without deleting the
 * original record (distinct from a merge). The conversation history, bot binding, and number
 * stay; only the patient member and the WhatsApp identity binding move.
 */
export function SwitchPatientDialog({ open, onOpenChange, threadId, currentPatientName, onSwitched }) {
  const [target, setTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setTarget(null);
      setError(null);
    }
  }, [open]);

  const targetName =
    target?.displayName ||
    `${target?.profile?.FirstName || ''} ${target?.profile?.LastName || ''}`.trim() ||
    'the selected patient';

  const handleSwitch = async () => {
    const targetId = String(target?.id || '').trim();
    if (!targetId || !threadId || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await switchThreadPatient({ threadId, targetPatientUserId: targetId });
      if (res.binding === 'membership-only') {
        toast.warning('Patient switched, but this patient has no WhatsApp number/id — future inbound messages won’t auto-route until one is added.');
      } else {
        toast.success('Conversation re-pointed to the new patient.');
      }
      onSwitched?.(res);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || 'Could not switch patient.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Switch patient
          </DialogTitle>
          <DialogDescription>
            Re-point this conversation from {currentPatientName ? <strong>{currentPatientName}</strong> : 'its current patient'} to another
            patient. The original record is kept — nothing is deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <PatientSearchDropdown onSelectPatient={setTarget} label="Switch to patient" autoFocus />

          {target && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">New patient: </span>
              <span className="font-medium text-foreground">{targetName}</span>
            </div>
          )}

          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0" />
            Message history, assigned team, and the WhatsApp number stay with the thread. Future inbound messages will resolve to the new patient.
          </p>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSwitch} disabled={!target || isSaving}>
            {isSaving ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Switch patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SwitchPatientDialog;
